import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import { useNotifications } from "@/hooks/useNotifications";
import { formatDate as formatDateUtil } from "@/utils/formatDate";

const StudentNotifications = ({ userId }) => {
  const router = useRouter();
  const [jobOverlayOpen, setJobOverlayOpen] = useState(false);
  const notificationRef = useRef(null);

  const {
    notificationCount,
    recentJobs,

    selectionNotifications,
    shortlistNotifications,

    selectionUnreadCount,
    shortlistUnreadCount,

    isLoadingJobs,
    fetchRecentJobs,

    markAsViewed,
    markSelectionAsRead,
    markShortlistAsRead,
  } = useNotifications(userId, "student");

  const unreadSelection = selectionNotifications.filter((n) => !n.isRead);
  const unreadShortlist = shortlistNotifications.filter((n) => !n.isRead);

  const totalUnreadCount = selectionUnreadCount + shortlistUnreadCount + notificationCount;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        setJobOverlayOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = (e) => {
    e.preventDefault();
    router.push("/notifications");
  };

  const handleNotificationMouseEnter = () => {
    if (totalUnreadCount > 0) {
      setJobOverlayOpen(true);

      if (notificationCount > 0 && recentJobs.length === 0) {
        fetchRecentJobs();
      }
    }
  };

  const handleViewAllJobs = (e) => {
    e.preventDefault();
    markAsViewed();
    setJobOverlayOpen(false);
    router.push("/jobs");
  };

  const handleViewShortlisted = (e) => {
    e.preventDefault();

    unreadShortlist.forEach((n) => markShortlistAsRead(n._id));

    setJobOverlayOpen(false);
    router.push("/student/student-applied-jobs?tab=shortlisted");
  };

  const handleViewSelection = (e) => {
    e.preventDefault();

    unreadSelection.forEach((n) => markSelectionAsRead(n._id));

    setJobOverlayOpen(false);
    router.push("/student/student-applied-jobs?tab=internship");
  };

  const renderJobOverlay = () => (
    <div className="rbt-job-notification-overlay">
      {unreadShortlist.length > 0 && (
        <>
          <div className="rbt-job-overlay-header">
            <h6 className="rbt-job-overlay-title">Shortlisted Jobs</h6>
            <span className="badge bg-success">{unreadShortlist.length}</span>
          </div>

          <ul className="rbt-job-overlay-list">
            {unreadShortlist.slice(0, 3).map((n) => (
              <li key={n._id} className="rbt-job-overlay-item unread">
                <div className="rbt-job-overlay-content">
                  <div className="rbt-job-overlay-title-text">
                    {n.title}
                  </div>
                  <div className="rbt-job-overlay-company">
                    {n.message}
                  </div>
                  <small className="text-muted">
                    {formatDateUtil(n.createdAt)}
                  </small>
                </div>
                <span className="unread-indicator"></span>
              </li>
            ))}
          </ul>

          <div className="rbt-job-overlay-footer">
            <a
              href="#"
              className="rbt-job-overlay-view-all"
              onClick={handleViewShortlisted}
            >
              View Shortlisted →
            </a>
          </div>

          <div className="rbt-job-overlay-divider"></div>
        </>
      )}

      {unreadSelection.length > 0 && (
        <>
          <div className="rbt-job-overlay-header">
            <h6 className="rbt-job-overlay-title">Selection Updates</h6>
            <span className="badge bg-info">{unreadSelection.length}</span>
          </div>

          <ul className="rbt-job-overlay-list">
            {unreadSelection.slice(0, 3).map((n) => (
              <li key={n._id} className="rbt-job-overlay-item unread">
                <div className="rbt-job-overlay-content">
                  <div className="rbt-job-overlay-title-text">
                    {n.title}
                  </div>
                  <div className="rbt-job-overlay-company">
                    {n.message}
                  </div>
                  <small className="text-muted">
                    {formatDateUtil(n.createdAt)}
                  </small>
                </div>
                <span className="unread-indicator"></span>
              </li>
            ))}
          </ul>

          <div className="rbt-job-overlay-footer">
            <a
              href="#"
              className="rbt-job-overlay-view-all"
              onClick={handleViewSelection}
            >
              View Details →
            </a>
          </div>

          <div className="rbt-job-overlay-divider"></div>
        </>
      )}

      {notificationCount > 0 && (
        <>
          <div className="rbt-job-overlay-header">
            <h6 className="rbt-job-overlay-title">Recent Job Postings</h6>
            <span className="badge bg-primary">{notificationCount}</span>
          </div>

          {isLoadingJobs ? (
            <div className="p-3">Loading...</div>
          ) : (
            <>
              <ul className="rbt-job-overlay-list">
                {recentJobs.map((job, i) => (
                  <li key={i} className="rbt-job-overlay-item">
                    <div className="rbt-job-overlay-content">
                      <div className="rbt-job-overlay-title-text">
                        {job.jobTitle || job.title}
                      </div>
                      <div className="rbt-job-overlay-company">
                        {job.company || job.companyName}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="rbt-job-overlay-footer">
                <a
                  href="#"
                  className="rbt-job-overlay-view-all"
                  onClick={handleViewAllJobs}
                >
                  View All Jobs →
                </a>
              </div>
            </>
          )}
        </>
      )}

      {totalUnreadCount === 0 && (
        <div className="p-3 text-center text-muted">
          No new notifications
        </div>
      )}
    </div>
  );

  return (
    <li
      className="access-icon rbt-notification-wrapper rbt-notification-position"
      ref={notificationRef}
    >
      <Link
        className="rbt-round-btn rbt-notification-btn"
        href="#"
        onClick={handleNotificationClick}
        onMouseEnter={handleNotificationMouseEnter}
        title="Notifications"
      >
        <i className="feather-bell"></i>
        {totalUnreadCount > 0 && (
          <span className="rbt-notification-count">
            {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
          </span>
        )}
      </Link>

      {jobOverlayOpen && renderJobOverlay()}
    </li>
  );
};

export default StudentNotifications;